import { useMemo } from "react";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { SurfaceChart } from "../ChartBlocks.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { DEFAULT_LAYER_COLORS } from "./shared.jsx";

export const kind = "chart";

export function Component({ spec, data, fillHeight = false }) {
    const resolved = useMemo(() => resolveChartData(spec, data), [spec, data]);

    const mode = resolved.mode || (Array.isArray(resolved.layers) && resolved.layers.length ? "stacked" : "line");
    const layoutHeight = Number.isFinite(resolved.height) ? resolved.height : 200;
    const chartClass =
        "h-full w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]";
    const titleNode = resolved.title ? (
        <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
            {resolved.title}
        </div>
    ) : null;
    // Compact-tooltip charts let the hover tooltip grow past the chart's bottom
    // edge; raise the chart's stacking context so that overflow paints above the
    // sibling content below it (e.g. the trace table) instead of behind it.
    const outerClass = [
        fillHeight ? "flex h-full min-h-0 flex-col" : "",
        resolved.compactStandingTooltip ? "relative z-30" : "",
    ].filter(Boolean).join(" ");
    const chartWrapperStyle = fillHeight ? undefined : { height: `${layoutHeight}px` };
    const chartWrapperClass = fillHeight ? "flex-1 min-h-0" : "";

    // Single-point degradation: a chart with one bucket per series
    // collapses to a vertical sliver in SurfaceChart. Fall back to a
    // share-of-max bar list — same shape the dashboard uses elsewhere
    // for "comparative count" data with no time axis.
    if (isSinglePoint(resolved, mode)) {
        const items = mode === "stacked"
            ? (resolved.layers || []).map((l, i) => ({
                  label: l.label || `Layer ${i + 1}`,
                  value: l.series?.[0] ?? 0,
              })).sort((a, b) => Number(b.value) - Number(a.value))
            : [{
                  label: resolved.seriesLabel || resolved.title || "Value",
                  value: resolved.series?.[0] ?? 0,
              }];
        return (
            <div className={outerClass}>
                {titleNode}
                <div className={chartWrapperClass} style={chartWrapperStyle}>
                    <KernelChartBars items={items} fillHeight={fillHeight} height={layoutHeight} />
                </div>
            </div>
        );
    }

    let chartEl;
    if (mode === "stacked" && Array.isArray(resolved.layers) && resolved.layers.length > 0) {
        const layers = resolved.layers.map((l, i) => ({
            id: l.label || `layer-${i}`,
            label: l.label || `Layer ${i + 1}`,
            color: sanitizeRenderColor(
                l.color,
                DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
            ),
            series: Array.isArray(l.series) ? l.series : [],
        }));
        chartEl = (
            <SurfaceChart
                mode="stacked"
                layers={layers}
                height={layoutHeight}
                interactiveDots
                range={resolved.range || null}
                compactStandingTooltip={resolved.compactStandingTooltip}
                className={chartClass}
            />
        );
    } else {
        // Line mode supports either a single `series` OR multi-line via
        // `layers` — the latter is how the spec doc tells agents to draw
        // overlays (rolling average + raw count, multi-metric comparison).
        // SurfaceChart's line branch only consumes one `series`, so we
        // map the first layer to that and route the rest through
        // `referenceSeriesList` with per-line color + full opacity so
        // they read as primary lines, not faint context outlines.
        const hasMultiLine = Array.isArray(resolved.layers) && resolved.layers.length > 0;
        const primary = hasMultiLine
            ? {
                  series: Array.isArray(resolved.layers[0]?.series) ? resolved.layers[0].series : [],
                  color: sanitizeRenderColor(
                      resolved.layers[0]?.color,
                      DEFAULT_LAYER_COLORS[0],
                  ),
                  label: resolved.layers[0]?.label || "Value",
              }
            : {
                  series: Array.isArray(resolved.series) ? resolved.series : [],
                  color: sanitizeRenderColor("var(--ink)"),
                  label: resolved.seriesLabel || resolved.title || "Value",
              };
        const extraLines = hasMultiLine
            ? resolved.layers.slice(1).map((l, i) => ({
                  // `key` is required by SurfaceChart — its tooltip
                  // maps `referenceSeriesList` rows to
                  // BaselineTooltipRow nodes keyed by `key`, and an
                  // undefined key triggers React's "child needs key"
                  // warning when a multi-line chart is hovered.
                  key: l?.label || `layer-${i + 1}`,
                  label: l?.label || `Layer ${i + 2}`,
                  series: Array.isArray(l?.series) ? l.series : [],
                  // Bump above the default 0.35 so overlay lines (e.g.
                  // 7-day rolling avg sitting on top of daily counts)
                  // read at the same prominence as the primary, not as
                  // faint scope-reference outlines.
                  stroke: sanitizeRenderColor(
                      l?.color,
                      DEFAULT_LAYER_COLORS[(i + 1) % DEFAULT_LAYER_COLORS.length],
                  ),
                  strokeOpacity: 0.9,
              }))
            : null;
        const legendLayers = hasMultiLine
            ? resolved.layers.map((l, i) => ({
                  id: l.label || `layer-${i}`,
                  label: l.label || `Layer ${i + 1}`,
                  color: sanitizeRenderColor(
                      l.color,
                      DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
                  ),
                  series: Array.isArray(l.series) ? l.series : [],
              }))
            : [{
                  id: "value",
                  label: primary.label,
                  color: primary.color,
                  series: primary.series,
              }];
        // Multi-line semantics: every layer is an equal-rank data
        // series. SurfaceChart's default y-domain logic was designed
        // for the scoped-view case (reference = parent total bounds
        // the child), so it pins the ceiling to the reference's max
        // when one is present. That clips the primary when an agent
        // orders layers as [daily counts, rolling avg] — daily counts
        // peak well above the rolling avg's max and overflow the
        // plot. Compute the envelope across every layer here so the
        // axis fits the whole chart regardless of layer order.
        const multiLineDomainMax = hasMultiLine
            ? resolved.layers.reduce((acc, layer) => {
                  const s = Array.isArray(layer.series) ? layer.series : [];
                  for (const v of s) {
                      if (Number.isFinite(v) && v > acc) acc = v;
                  }
                  return acc;
              }, 0)
            : null;
        const explicitDomainMax = Number.isFinite(resolved.domainMax) ? resolved.domainMax : null;
        // Only pin the y-domain when there's positive data. An all-zero
        // multi-line chart (e.g. a "default activity surface" before any
        // signal has fired) computes multiLineDomainMax === 0, which would
        // pin the ceiling to 0 and degenerate the plot — fall back to auto.
        const resolvedDomainMax = explicitDomainMax ?? (multiLineDomainMax > 0 ? multiLineDomainMax : null);
        chartEl = (
            <SurfaceChart
                mode="line"
                series={primary.series}
                stroke={primary.color}
                fill={primary.color}
                fillOpacity={hasMultiLine ? 0 : 0.18}
                fillPattern={!hasMultiLine}
                height={layoutHeight}
                interactiveDots
                range={resolved.range || null}
                legendLayers={legendLayers}
                referenceSeriesList={extraLines}
                domainMax={resolvedDomainMax}
                compactStandingTooltip={resolved.compactStandingTooltip}
                className={chartClass}
            />
        );
    }

    return (
        <div className={outerClass}>
            {titleNode}
            <div className={chartWrapperClass} style={chartWrapperStyle}>{chartEl}</div>
        </div>
    );
}

function resolveChartData(spec, data) {
    if (data == null) return spec;

    if (Array.isArray(data) && data.length && isPlainRow(data[0])) {
        if (Array.isArray(spec.layerFields) && spec.layerFields.length) {
            const layers = spec.layerFields.map((cfg) => ({
                label: cfg.label || cfg.field || "Layer",
                series: data.map((r) => Number(r?.[cfg.field]) || 0),
                color: cfg.color,
            }));
            return { ...spec, layers, mode: spec.mode || "stacked" };
        }
        const field = spec.valueField || autoNumericField(data[0]);
        if (field) {
            const series = data.map((r) => Number(r?.[field]) || 0);
            return { ...spec, series, mode: spec.mode || "line" };
        }
        return spec;
    }

    if (Array.isArray(data) && (data.length === 0 || typeof data[0] === "number")) {
        return { ...spec, series: data, mode: spec.mode || "line" };
    }
    if (typeof data === "object" && Array.isArray(data.layers)) {
        return { ...spec, layers: data.layers, mode: spec.mode || "stacked" };
    }
    if (typeof data === "object" && Array.isArray(data.series)) {
        return { ...spec, series: data.series, mode: spec.mode || "line" };
    }
    return spec;
}

function isPlainRow(v) {
    return v && typeof v === "object" && !Array.isArray(v);
}

function autoNumericField(sampleRow) {
    if (!sampleRow || typeof sampleRow !== "object") return null;
    const preferred = ["count", "value", "total", "n"];
    for (const name of preferred) {
        if (name in sampleRow && Number.isFinite(Number(sampleRow[name]))) return name;
    }
    for (const [key, value] of Object.entries(sampleRow)) {
        if (value == null) continue;
        const n = Number(value);
        if (Number.isFinite(n) && typeof value !== "boolean") return key;
    }
    return null;
}

function isSinglePoint(spec, mode) {
    if (mode === "stacked" && Array.isArray(spec.layers) && spec.layers.length > 0) {
        return spec.layers.every((l) => !Array.isArray(l.series) || l.series.length <= 1);
    }
    if (mode === "line" && Array.isArray(spec.series)) {
        return spec.series.length <= 1;
    }
    return false;
}

function KernelChartBars({ items, height, fillHeight = false }) {
    const max = items.reduce((m, it) => Math.max(m, Number(it.value) || 0), 0);
    return (
        <div
            className={`flex ${fillHeight ? "h-full min-h-0" : ""} flex-col gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px]`}
            style={fillHeight ? undefined : { height: `${height}px` }}
        >
            {items.map((it, i) => {
                const value = Number(it.value) || 0;
                const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
                return (
                    <div
                        key={i}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-[8px]"
                    >
                        <OverflowTooltip as="strong" text={it.label != null ? String(it.label) : ""} className="min-w-0 text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]">
                            {it.label}
                        </OverflowTooltip>
                        <strong className="justify-self-end whitespace-nowrap text-[12px] leading-[1.08] font-medium tabular-nums text-[color:var(--ink)]">
                            {value}
                        </strong>
                        <div className="col-span-2 mt-[2px] flex h-[3px] w-full items-center">
                            {pct > 0 ? (
                                <div
                                    className="h-full rounded-[1px] bg-[color:var(--green)]"
                                    style={{ width: `${pct}%`, opacity: 0.45 }}
                                />
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
